'use client';

import { datadogRum } from '@datadog/browser-rum';
import { usePathname } from 'next/navigation';
import { type PropsWithChildren, Suspense, useEffect } from 'react';
import { STATIC_EXPERIMENT_CONFIG } from '../experiments';

type TrackerProps = {
  serverSideExperimentDecision: string;
};

// Isolated so `usePathname()` doesn't gate `children` behind its own Suspense boundary — under
// cacheComponents, a client hook like this one needs to resolve during static generation, and
// without a boundary around it specifically, everything beneath it (including unrelated content)
// bails out to client-side rendering. See BNK-1108.
function StaticExperimentTracker({ serverSideExperimentDecision }: TrackerProps) {
  const pathname = usePathname();
  const staticExperimentName = STATIC_EXPERIMENT_CONFIG[pathname];

  useEffect(() => {
    if (staticExperimentName) {
      datadogRum.addFeatureFlagEvaluation(
        // The hyphen is not allowed according to DD documentation
        //  https://docs.datadoghq.com/real_user_monitoring/guide/setup-feature-flag-data-collection/?tab=browser#feature-flag-naming
        staticExperimentName?.replace('-', '_'),
        serverSideExperimentDecision,
      );
      datadogRum.addAction('Static ABSmartly Bucketing', {
        staticExperimentName,
      });
    }
  }, [staticExperimentName]);

  return null;
}

type Props = PropsWithChildren<{
  serverSideExperimentDecision: string;
}>;

export function LogStaticExperiments({ children, serverSideExperimentDecision }: Props) {
  return (
    <>
      <Suspense fallback={null}>
        <StaticExperimentTracker serverSideExperimentDecision={serverSideExperimentDecision} />
      </Suspense>
      {children}
    </>
  );
}
